import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExternalLink, Loader2 } from "lucide-react";
import { JsonBlock } from "@/components/ui/json-block";
import { formatRelativeTime } from "@/lib/utils";
import { useAddressIssue, useScrapeUrl } from "@/hooks/useValidation";
import type {
  ValidationIssue,
  ResolutionType,
  ResolutionRequest,
} from "@/types/validation";
import { toast } from "sonner";

interface IssueDetailsDialogProps {
  issue: ValidationIssue | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScrapeUrl: (url: string) => void;
}

export function IssueDetailsDialog({
  issue,
  open,
  onOpenChange,
  onScrapeUrl,
}: IssueDetailsDialogProps) {
  const [resolutionType, setResolutionType] = useState<ResolutionType>("fixed");
  const [resolutionNotes, setResolutionNotes] = useState("");

  const addressMutation = useAddressIssue();
  const scrapeMutation = useScrapeUrl();

  if (!issue) return null;

  const handleResolve = async () => {
    try {
      const resolution: ResolutionRequest = {
        status: "resolved",
        resolution_type: resolutionType,
      };
      if (resolutionNotes) {
        resolution.resolution_notes = resolutionNotes;
      }
      await addressMutation.mutateAsync({
        issueId: issue.id,
        resolution,
      });
      toast.success("Issue marked as resolved");
      setResolutionNotes("");
      onOpenChange(false);
    } catch (error) {
      toast.error(
        `Failed to resolve issue: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  };

  const handleResolveAndScrape = async () => {
    try {
      const resolution: ResolutionRequest = {
        status: "resolved",
        resolution_type: resolutionType,
      };
      if (resolutionNotes) {
        resolution.resolution_notes = resolutionNotes;
      }
      await addressMutation.mutateAsync({
        issueId: issue.id,
        resolution,
      });
      setResolutionNotes("");
      onOpenChange(false);
      await scrapeMutation.mutateAsync({ url: issue.url });
      toast.success("Issue resolved and scrape started");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unknown error");
    }
  };

  const handleScrapeUrl = () => {
    onScrapeUrl(issue.url);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Issue Details
            <Badge
              variant={issue.level === "error" ? "destructive" : "secondary"}
            >
              {issue.level}
            </Badge>
            <Badge
              variant={issue.status === "addressed" ? "default" : "secondary"}
            >
              {issue.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 min-w-0">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Validation Type</Label>
              <p className="text-sm mt-1">{issue.validation_type}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Source</Label>
              <p className="text-sm mt-1 uppercase">{issue.source}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Scope</Label>
              <p className="text-sm mt-1 capitalize">{issue.scope}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Scraped At</Label>
              <p className="text-sm mt-1">
                {formatRelativeTime(issue.scraped_at)}
              </p>
            </div>
          </div>

          <div>
            <Label className="text-muted-foreground">URL</Label>
            <a
              href={issue.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:opacity-90 flex items-center gap-1 mt-1 break-all"
            >
              <span className="break-all">{issue.url}</span>
              <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </div>

          {issue.product_id && (
            <div>
              <Label className="text-muted-foreground">Product ID</Label>
              <p className="text-sm mt-1">
                <Link
                  to={{
                    pathname: "/product-viewer",
                    search: `?type=product&id=${issue.product_id}`,
                  }}
                  className="text-primary hover:underline focus:underline focus:outline-none"
                  onClick={() => onOpenChange(false)}
                >
                  {issue.product_id}
                </Link>
              </p>
            </div>
          )}

          {issue.variant_id && (
            <div>
              <Label className="text-muted-foreground">Variant ID</Label>
              <p className="text-sm mt-1">
                <Link
                  to={{
                    pathname: "/product-viewer",
                    search: `?type=variant&id=${issue.variant_id}`,
                  }}
                  className="text-primary hover:underline focus:underline focus:outline-none"
                  onClick={() => onOpenChange(false)}
                >
                  {issue.variant_id}
                </Link>
              </p>
            </div>
          )}

          {issue.external_variant_id && (
            <div>
              <Label className="text-muted-foreground">
                External Variant ID
              </Label>
              <p className="text-sm mt-1">{issue.external_variant_id}</p>
            </div>
          )}

          <div>
            <Label className="text-muted-foreground">Message</Label>
            <p className="text-sm mt-1 whitespace-pre-wrap break-words bg-muted p-3 rounded max-w-full">
              {issue.message}
            </p>
          </div>

          {issue.details && Object.keys(issue.details).length > 0 && (
            <div>
              <Label className="text-muted-foreground">
                Additional Details
              </Label>
              <JsonBlock
                value={issue.details}
                className="text-xs mt-1 bg-background p-3 rounded overflow-auto break-words whitespace-pre-wrap max-w-full"
              />
            </div>
          )}

          {issue.status === "addressed" && (
            <div className="border-t border-border pt-4">
              <h3 className="font-medium mb-2">Resolution Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">
                    Resolution Type
                  </Label>
                  <p className="text-sm mt-1 capitalize">
                    {issue.resolution_type?.replace("_", " ")}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Addressed At</Label>
                  <p className="text-sm mt-1">
                    {issue.addressed_at
                      ? formatRelativeTime(issue.addressed_at)
                      : "N/A"}
                  </p>
                </div>
              </div>
              {issue.resolution_notes && (
                <div className="mt-2 min-w-0">
                  <Label className="text-muted-foreground">
                    Resolution Notes
                  </Label>
                  <p className="text-sm mt-1 bg-muted p-3 rounded break-words">
                    {issue.resolution_notes}
                  </p>
                </div>
              )}
            </div>
          )}

          {issue.status === "unresolved" && (
            <div className="border-t border-border pt-4">
              <h3 className="font-medium mb-4">Resolve Issue</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="resolution-type">Resolution Type</Label>
                  <Select
                    value={resolutionType}
                    onValueChange={(v) =>
                      setResolutionType(v as ResolutionType)
                    }
                  >
                    <SelectTrigger id="resolution-type" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed</SelectItem>
                      <SelectItem value="false_positive">
                        False Positive
                      </SelectItem>
                      <SelectItem value="wont_fix">Won't Fix</SelectItem>
                      <SelectItem value="needs_investigation">
                        Needs Investigation
                      </SelectItem>
                      <SelectItem value="override">Override</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="resolution-notes">
                    Resolution Notes (optional)
                  </Label>
                  <Textarea
                    id="resolution-notes"
                    placeholder="Add any notes about how this was resolved..."
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap min-w-0">
          <Button variant="outline" onClick={handleScrapeUrl}>
            Scrape URL Again
          </Button>
          {issue.status === "unresolved" && (
            <>
              <Button
                onClick={handleResolve}
                disabled={addressMutation.isPending}
              >
                {addressMutation.isPending && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                Mark as Resolved
              </Button>
              <Button
                onClick={handleResolveAndScrape}
                disabled={addressMutation.isPending || scrapeMutation.isPending}
              >
                {(addressMutation.isPending || scrapeMutation.isPending) && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                Resolve and Scrape
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
